// Helpers compartidos del módulo Signage (acceso REST/RPC al esquema signage).
import { apiClient } from '@/lib/api/client';

export const SIG = { headers: { 'Accept-Profile': 'signage', 'Content-Profile': 'signage' } };

export const sigList = <T,>(q: string): Promise<T[]> =>
  apiClient.get<T[]>(`/rest/signage.${q}`, SIG).then((r) => r.data);

export const sigRpc = <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  apiClient.post(`/rpc/${fn}`, body, SIG).then((r) => r.data as T);

export const sigInsert = <T,>(tabla: string, body: Record<string, unknown>): Promise<T> =>
  apiClient.post(`/rest/signage.${tabla}`, body, { headers: { ...SIG.headers, Prefer: 'return=representation' } }).then((r) => (r.data as T[])[0]);

export const sigUpdate = (tabla: string, id: string, body: Record<string, unknown>): Promise<void> =>
  apiClient.patch(`/rest/signage.${tabla}?id=eq.${id}`, body, SIG).then(() => undefined);

export const sigSoftDelete = (tabla: string, id: string): Promise<void> =>
  apiClient.patch(`/rest/signage.${tabla}?id=eq.${id}`, { deleted_at: new Date().toISOString(), es_activo: false }, SIG).then(() => undefined);

export interface RefRow { id: string; nombre: string }

export const DIAS = [
  { v: null, l: 'Todos los días' }, { v: 1, l: 'Lunes' }, { v: 2, l: 'Martes' }, { v: 3, l: 'Miércoles' },
  { v: 4, l: 'Jueves' }, { v: 5, l: 'Viernes' }, { v: 6, l: 'Sábado' }, { v: 0, l: 'Domingo' },
];

export const TIPOS_ESCENA = [
  { v: 'catalogo', l: 'Catálogo (hero por producto)' },
  { v: 'promociones', l: 'Promociones (solo productos con descuento)' },
  { v: 'top_ventas', l: 'Más vendidos (ranking por ventas)' },
  { v: 'carta', l: 'Menú a la carta (3 columnas)' },
  { v: 'mensaje', l: 'Mensaje (título + texto)' },
  { v: 'imagen', l: 'Imagen a pantalla completa (biblioteca)' },
  { v: 'qr', l: 'QR phygital (compra en línea)' },
  { v: 'video', l: 'Video (URL / YouTube / Vimeo)' },
  { v: 'feed', l: 'Feed externo (reloj / clima / cuenta)' },
  { v: 'layout', l: 'Diseño por zonas (split / L-bar estilo CNN)' },
  { v: 'cross_sell', l: 'Cross-sell en vivo (según el carrito del local)' },
  { v: 'combo', l: 'Combos de temporada (kits/packs curados)' },
  { v: 'cupon', l: 'Cupón canjeable en caja (código con vigencia por hora)' },
  { v: 'por_vencer', l: 'Por vencer (liquidación anti-merma por lote)' },
  { v: 'salud', l: 'Salud del día (consejo + clima → producto)' },
  { v: 'lentos', l: 'Lentos / liquidación (ABC: promociona lento movimiento)' },
  { v: 'social', l: 'Redes sociales (muro de posts: Instagram/Facebook/TikTok/X)' },
  { v: 'ventas_hoy', l: 'Lo más llevado HOY (contador social del local)' },
  { v: 'turnos', l: 'Turnos de hoy (médicos con cupos — captación de citas)' },
  { v: 'resenas', l: 'Reseñas de pacientes (calificaciones internas 4-5★)' },
  { v: 'marco', l: 'Marco roto (el producto "sale" del marco — efecto 3D)' },
];

// Ventanas recurrentes de días de pago (420): el retail EC respira por quincena
export const MOMENTOS = [
  { v: '', l: 'Siempre' },
  { v: 'pago', l: 'Días de pago (14-16 y fin de mes)' },
  { v: 'quincena', l: 'Solo quincena (14 al 16)' },
  { v: 'fin_mes', l: 'Solo fin de mes (últimos 2 días + 1 y 2)' },
];

export const CLIMA_COND = [
  { v: '', l: 'Siempre' },
  { v: 'lluvia', l: 'Solo si llueve (o lloverá en 3h)' },
  { v: 'frio', l: 'Solo con frío (≤16°)' },
  { v: 'calor', l: 'Solo con calor (≥29°)' },
  { v: 'sol', l: 'Solo con UV alto' },
];

export const REDES_SOCIALES = [
  { v: 'instagram', l: 'Instagram' },
  { v: 'facebook', l: 'Facebook' },
  { v: 'tiktok', l: 'TikTok' },
  { v: 'x', l: 'X (Twitter)' },
];

export const FEEDS = [
  { v: 'reloj', l: 'Reloj y fecha' },
  { v: 'clima', l: 'Clima (Open-Meteo, sin API key)' },
  { v: 'cuenta', l: 'Cuenta regresiva a una fecha' },
  { v: 'noticias', l: 'Noticias (RSS, requiere worker)' },
];

// ---- Biblioteca de medios --------------------------------------------------
export interface MediaAsset {
  id: string; tipo: 'imagen' | 'video'; nombre: string; hash: string;
  datos: string | null; url: string | null; poster: string | null; mime: string | null;
  ancho: number | null; alto: number | null; foco_x: number; foco_y: number; bytes: number; dur: number | null;
}

export async function sha256Hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function fileToImage(file: File): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const u = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(u); res(img); };
    img.onerror = () => { URL.revokeObjectURL(u); rej(new Error('No se pudo leer la imagen')); };
    img.src = u;
  });
}

// Reescala (máx 1920 px) + convierte a WebP (fallback JPEG en Safari) + hash de contenido.
// Con `recortar`, intenta quitar el fondo (foto de estudio) y exporta con canal alfa
// (WebP con alfa; el fallback pasa a PNG porque JPEG no tiene transparencia).
export async function procesarImagen(file: File, maxLado = 1920, recortar = false): Promise<Partial<MediaAsset> & { recortado?: boolean }> {
  const img = await fileToImage(file);
  const escala = Math.min(1, maxLado / Math.max(img.width, img.height));
  const w = Math.round(img.width * escala), h = Math.round(img.height * escala);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
  let lienzo: HTMLCanvasElement = canvas;
  let recortado = false;
  if (recortar) {
    const rec = recortarFondo(canvas);
    if (rec) { lienzo = rec; recortado = true; }
  }
  let datos = lienzo.toDataURL('image/webp', 0.82);
  let mime = 'image/webp';
  if (!datos.startsWith('data:image/webp')) {
    datos = recortado ? lienzo.toDataURL('image/png') : lienzo.toDataURL('image/jpeg', 0.85);
    mime = recortado ? 'image/png' : 'image/jpeg';
  }
  const bytes = Math.round(((datos.length - datos.indexOf(',') - 1) * 3) / 4);
  return { tipo: 'imagen', datos, mime, ancho: lienzo.width, alto: lienzo.height, bytes, hash: await sha256Hex(datos), recortado };
}

// ---- Quita-fondo (recorte de producto para el efecto "marco roto") ----------
// Heurística de foto de estudio: si TODO el borde de la imagen es de un color
// uniforme y claro (fondo blanco/gris de catálogo), un flood-fill DESDE los
// bordes lo vuelve transparente — respeta los blancos INTERNOS del producto
// (etiquetas, tapas) porque solo borra lo conectado al borde — y suaviza el
// contorno. Si la foto es ambientada (fondo con textura/oscuro) devuelve null
// y el llamador degrada con dignidad (foto enmarcada, sin efecto pop-out).
// Si la imagen YA trae transparencia, la devuelve tal cual (recorte previo).
export function recortarFondo(canvas: HTMLCanvasElement): HTMLCanvasElement | null {
  const w = canvas.width, h = canvas.height;
  if (w < 40 || h < 40) return null;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const im = ctx.getImageData(0, 0, w, h);
  const d = im.data;

  // 1) Muestrear el borde: ¿hay alfa previo? ¿color promedio claro y uniforme?
  let n = 0, sr = 0, sg = 0, sb = 0, conAlfa = false;
  const muestras: number[] = [];
  const paso = Math.max(1, Math.floor((w + h) / 600));
  const tomar = (x: number, y: number) => {
    const i = (y * w + x) * 4;
    if (d[i + 3] < 250) { conAlfa = true; return; }
    sr += d[i]; sg += d[i + 1]; sb += d[i + 2]; n += 1; muestras.push(i);
  };
  for (let x = 0; x < w; x += paso) { tomar(x, 0); tomar(x, h - 1); }
  for (let y = 0; y < h; y += paso) { tomar(0, y); tomar(w - 1, y); }
  if (conAlfa) return canvas;                    // ya viene recortada
  if (!n) return null;
  const br = sr / n, bg = sg / n, bb = sb / n;
  if ((br + bg + bb) / 3 < 165) return null;     // fondo oscuro: no es estudio claro
  let desv = 0;
  for (const i of muestras) desv += Math.abs(d[i] - br) + Math.abs(d[i + 1] - bg) + Math.abs(d[i + 2] - bb);
  if (desv / n > 36) return null;                // borde no uniforme (foto ambientada)

  // 2) Flood-fill desde los 4 bordes sobre píxeles parecidos al fondo (tolera sombras suaves)
  const TOL = 132;                               // distancia L1 (r+g+b) aceptada
  const fondo = new Uint8Array(w * h);
  const cola: number[] = [];
  const probar = (p: number) => {
    if (fondo[p]) return;
    const i = p * 4;
    if (Math.abs(d[i] - br) + Math.abs(d[i + 1] - bg) + Math.abs(d[i + 2] - bb) <= TOL) { fondo[p] = 1; cola.push(p); }
  };
  for (let x = 0; x < w; x++) { probar(x); probar((h - 1) * w + x); }
  for (let y = 0; y < h; y++) { probar(y * w); probar(y * w + w - 1); }
  while (cola.length) {
    const p = cola.pop()!;
    const x = p % w;
    if (x > 0) probar(p - 1);
    if (x < w - 1) probar(p + 1);
    if (p >= w) probar(p - w);
    if (p < (h - 1) * w) probar(p + w);
  }

  // 3) Sanidad: el fondo debe pesar (si no, no hay efecto) y debe QUEDAR producto
  let nf = 0;
  for (let p = 0; p < fondo.length; p++) nf += fondo[p];
  const frac = nf / (w * h);
  if (frac < 0.12 || frac > 0.985) return null;

  // 4) Alfa con contorno suavizado (blur 3×3 del canal alfa) + bbox del producto
  const alfa = new Uint8ClampedArray(w * h);
  for (let p = 0; p < alfa.length; p++) alfa[p] = fondo[p] ? 0 : 255;
  let minX = w, minY = h, maxX = 0, maxY = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const p = y * w + x;
      let s = 0, c = 0;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= h) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx;
          if (xx < 0 || xx >= w) continue;
          s += alfa[yy * w + xx]; c += 1;
        }
      }
      const a = Math.round(s / c);
      d[p * 4 + 3] = a;
      if (a > 16) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; }
    }
  }
  if (maxX <= minX || maxY <= minY) return null;
  ctx.putImageData(im, 0, 0);

  // 5) Recortar al bbox con un margen del 2% (el pop-out escala mejor sin aire muerto)
  const mx = Math.round((maxX - minX) * 0.02), my = Math.round((maxY - minY) * 0.02);
  minX = Math.max(0, minX - mx); maxX = Math.min(w - 1, maxX + mx);
  minY = Math.max(0, minY - my); maxY = Math.min(h - 1, maxY + my);
  const out = document.createElement('canvas');
  out.width = maxX - minX + 1; out.height = maxY - minY + 1;
  out.getContext('2d')!.drawImage(canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}

// Recorta el fondo de una imagen data-URI (o URL misma-origen). Devuelve el
// data-URI recortado (WebP con alfa; PNG si el navegador no exporta WebP),
// el MISMO src si ya venía con transparencia, o null si no es recortable.
export async function recortarFondoDataUri(src: string, maxLado = 1200): Promise<string | null> {
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image();
    i.onload = () => res(i); i.onerror = () => rej(new Error('No se pudo leer la imagen'));
    i.src = src;
  });
  const escala = Math.min(1, maxLado / Math.max(img.width || 1, img.height || 1));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.width * escala));
  canvas.height = Math.max(1, Math.round(img.height * escala));
  canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
  const rec = recortarFondo(canvas);
  if (!rec) return null;
  if (rec === canvas) return src;                // ya era transparente: úsala tal cual
  let datos = rec.toDataURL('image/webp', 0.84);
  if (!datos.startsWith('data:image/webp')) datos = rec.toDataURL('image/png');
  return datos;
}
